/**
 * =============================================================================
 * Componente: CampaignCard
 * Versão: 1.1.0 (2026-08-17) — prop `showKpis` para ocultar o grid de KPIs
 *
 * Descrição:
 *   Card premium da página "Minhas Campanhas" do portal do afiliado.
 *   Layout inspirado na página de Materiais (galeria com capa em destaque),
 *   ampliado com:
 *     - KPIs: cliques, indicações, conversões, total ganho (R$).
 *     - URL pública pronta para copiar.
 *     - Ações: QR Code, Compartilhar (WhatsApp), Regras (modelo de contrato).
 *
 *   - Capa pode ser imagem ou vídeo. Quando ausente, mostra hero gradiente.
 *   - Link pessoal é criado *lazy* na primeira interação (copy/QR/share)
 *     via `useEnsureCampaignLink`, evitando N+1 no carregamento da página.
 * =============================================================================
 */
import { useMemo, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  Check,
  QrCode,
  Share2,
  FileText,
  Loader2,
  Megaphone,
  Play,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  type MyCampaignLink,
  useEnsureCampaignLink,
} from '@/hooks/useAffiliateMyCampaigns';
import { useAffiliateLinkActions } from '@/hooks/useAffiliateLinkActions';
import { QrCodeDialog } from '@/components/affiliate/QrCodeDialog';
import { CampaignTermsDialog } from '@/components/affiliate/CampaignTermsDialog';
import { CampaignCountdownBadge } from '@/components/affiliate/CampaignCountdownBadge';
import { useCountdown } from '@/hooks/useCountdown';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { KpiInfoTooltip } from '@/components/affiliate/shared/KpiInfoTooltip';

interface Props {
  campaign: MyCampaignLink;
  /** Origem pública (ex.: https://app.com). Default: window.location.origin. */
  publicOrigin?: string;
  /** Exibe (default) ou oculta o grid 2x2 de KPIs para economizar espaço. */
  showKpis?: boolean;
}


/**
 * Constrói a URL pública /r/<short_code|slug>. Quando o link pessoal ainda
 * não existe, retorna null (a UI mostra um placeholder e cria sob demanda).
 */
function buildPublicUrl(c: MyCampaignLink, origin: string): string | null {
  const code = c.short_code ?? c.slug;
  if (!code) return null;
  return `${origin.replace(/\/$/, '')}/r/${code}`;
}

/** Formatador BRL/etc. compacto. */
function formatMoney(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `R$ ${value.toFixed(2)}`;
  }
}

export function CampaignCard({ campaign: c, publicOrigin, showKpis = true }: Props) {
  const origin =
    publicOrigin ?? (typeof window !== 'undefined' ? window.location.origin : '');

  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  // Estado do preview ampliado de vídeo (lightbox próprio do app, evita
  // depender do botão nativo de fullscreen do <video>, que cortava bordas).
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);

  const { shareWhatsapp, isSharingWhatsapp } = useAffiliateLinkActions();
  const ensureLink = useEnsureCampaignLink();
  const queryClient = useQueryClient();

  const publicUrl = useMemo(() => buildPublicUrl(c, origin), [c, origin]);

  // Acompanha o status do prazo da campanha. Quando há `ends_at`, o
  // próprio badge dispara `onEnd` ao expirar; aqui usamos para esmaecer
  // o card e desabilitar ações em tempo real (sem esperar refetch).
  const cdState = useCountdown(c.ends_at, { startsAt: c.starts_at });
  const isEnded = cdState.status === 'ended';

  /** Invalida a listagem para que o backend remova a campanha encerrada. */
  const handleCountdownEnd = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['affiliate-my-campaigns'] });
  }, [queryClient]);

  /**
   * Garante a existência do link pessoal antes da ação (copy/QR/share).
   * Retorna o link_id resolvido — usa o existente quando já houver.
   */
  const ensureLinkId = async (): Promise<string | null> => {
    if (c.link_id) return c.link_id;
    try {
      const link = await ensureLink.mutateAsync({ campaignId: c.campaign_id });
      return link.id;
    } catch {
      toast.error('Não foi possível preparar seu link pessoal.');
      return null;
    }
  };

  const handleCopy = async () => {
    const linkId = await ensureLinkId();
    if (!linkId) return;
    // Após ensureLink, a URL pública está atualizada via cache otimista,
    // mas precisamos reconstruir com o estado atualizado (re-render virá).
    const code = c.short_code ?? c.slug;
    const url = code ? `${origin.replace(/\/$/, '')}/r/${code}` : null;
    if (!url) {
      toast.error('Link indisponível no momento.');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const handleQrOpen = async () => {
    const linkId = await ensureLinkId();
    if (!linkId) return;
    setQrOpen(true);
  };

  const handleShareWhatsapp = async () => {
    const linkId = await ensureLinkId();
    if (!linkId) return;
    try {
      const res = await shareWhatsapp({ linkId });
      window.open(res.whatsapp_url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Não foi possível abrir o WhatsApp.');
    }
  };

  const isVideo = c.cover_media_type === 'video';
  const hasCover = !!c.cover_media_url;

  return (
    <div
      className={cn(
        'aff-card overflow-hidden flex flex-col transition-opacity',
        isEnded && 'opacity-70',
      )}
    >
      {/* ----------------------------------------------------------------- */}
      {/* Capa (image | video | fallback gradiente)                          */}
      {/* ----------------------------------------------------------------- */}
      {/*
        Capa com altura fixa (h-44 = 176px). Não usar apenas `aspect-video`,
        pois em grids com altura igualada e/ou imagens com proporções muito
        verticais o container pode ser empurrado pelo conteúdo natural da
        mídia, desconfigurando o card. Altura fixa + `object-cover` nos
        filhos garante uniformidade absoluta entre todos os cards.
      */}
      <div className="relative h-44 w-full shrink-0 overflow-hidden bg-[hsl(var(--aff-surface-2))]">
        {hasCover && isVideo ? (
          // Thumbnail clicável: o player nativo (com controls) é exibido
          // apenas dentro do Dialog de preview, garantindo `object-contain`
          // em fundo preto e eliminando o corte de bordas em tela cheia.
          <button
            type="button"
            onClick={() => setVideoPreviewOpen(true)}
            aria-label={`Reproduzir vídeo: ${c.campaign_name}`}
            className="group relative h-full w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aff-primary))]"
          >
            <video
              src={c.cover_media_url!}
              preload="metadata"
              muted
              playsInline
              // Desabilita PiP/fullscreen nativo no thumbnail.
              disablePictureInPicture
              className="h-full w-full object-cover pointer-events-none"
            />
            {/* Overlay com botão Play para indicar interação */}
            <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/40">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/95 shadow-lg transition-transform group-hover:scale-105">
                <Play className="h-5 w-5 translate-x-[1px] text-black" fill="currentColor" />
              </span>
            </span>
          </button>
        ) : hasCover ? (
          <img
            src={c.cover_media_url!}
            alt={c.campaign_name}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            className="h-full w-full flex items-center justify-center"
            style={{ background: 'var(--aff-gradient-hero)' }}
          >
            <Megaphone className="h-12 w-12 text-[hsl(var(--aff-primary-foreground))] opacity-90" />
          </div>
        )}

        {/* Badge de tipo (Vídeo/Imagem) removido — distinção visual já é óbvia. */}

        {/* Status (canto sup. dir.) */}
        <span className="absolute right-2 top-2 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--aff-bg)/0.92)] backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--aff-foreground))] shadow-sm">
          <span
            aria-hidden
            className={cn(
              'h-2 w-2 rounded-full shrink-0',
              c.is_active
                ? 'bg-[hsl(var(--aff-success))]'
                : 'bg-[hsl(var(--aff-muted-foreground))]',
            )}
          />
          {c.is_active ? 'Ativo' : 'Inativo'}
        </span>

        {/* Contador regressivo (escassez) — canto inferior esquerdo da capa.
            Renderizado apenas quando há `ends_at` definido. */}
        {(c.ends_at || c.starts_at) && (
          <div className="absolute bottom-2 left-2 z-10">
            <CampaignCountdownBadge
              endsAt={c.ends_at}
              startsAt={c.starts_at}
              onEnd={handleCountdownEnd}
            />
          </div>
        )}
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Conteúdo                                                          */}
      {/* ----------------------------------------------------------------- */}
      <div className="p-4 space-y-3 flex-1 flex flex-col">
        <div className="space-y-1">
          <h3 className="font-bold leading-tight line-clamp-1 text-[hsl(var(--aff-foreground))]">
            {c.campaign_name}
          </h3>
          {c.campaign_description && (
            <p className="line-clamp-2 text-xs text-[hsl(var(--aff-muted-foreground))]">
              {c.campaign_description}
            </p>
          )}
        </div>

        {/*
          KPIs em matriz 2x2 editorial. Para garantir hairlines simétricos
          (uma linha vertical entre colunas e uma horizontal entre linhas),
          desenhamos as bordas POR CÉLULA via posição:
            - célula 1 (top-left)    : border-r + border-b
            - célula 2 (top-right)   : border-b
            - célula 3 (bottom-left) : border-r
            - célula 4 (bottom-right): nenhuma
          O container fornece o anel externo via border + rounded.
          Essa técnica evita os bugs do `divide-x/divide-y` em grid 2x2
          (que rendia linhas faltando/duplicadas dependendo do navegador).
        */}
        {/* Renderizado apenas quando `showKpis` (default true). No dashboard
            o carrossel oculta este bloco para economizar espaço vertical. */}
        {showKpis && (
        <div className="grid grid-cols-2 grid-rows-2 overflow-hidden rounded-lg border border-[hsl(var(--aff-border))]">
          <div className="border-b border-r border-[hsl(var(--aff-border))]">
            <KpiCell
              label="cliques"
              value={c.clicks_count.toLocaleString('pt-BR')}
              hint="Total de cliques no link desta campanha (acumulado, sem filtro de período)."
            />
          </div>
          <div className="border-b border-[hsl(var(--aff-border))]">
            <KpiCell
              label="indicações"
              value={c.referrals_count.toLocaleString('pt-BR')}
              hint="Total de leads gerados por esta campanha (acumulado, sem filtro de período)."
            />
          </div>
          <div className="border-r border-[hsl(var(--aff-border))]">
            <KpiCell
              label="conversões"
              value={c.conversions_count.toLocaleString('pt-BR')}
              hint="Quantidade de indicações desta campanha que foram convertidas em negócio fechado (acumulado)."
            />
          </div>
          <div>
            <KpiCell
              label="ganho"
              value={formatMoney(c.earned_amount, c.currency)}
              hint="Soma das comissões aprovadas geradas por esta campanha (acumulado, sem filtro de período)."
            />
          </div>
        </div>
        )}


        {/* URL pública */}
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
          <code className="flex-1 truncate text-xs font-mono text-foreground">
            {publicUrl ?? 'Toque em copiar para gerar seu link'}
          </code>
          <button
            type="button"
            onClick={handleCopy}
            disabled={ensureLink.isPending || isEnded}
            aria-label="Copiar link"
            title={isEnded ? 'Campanha encerrada' : 'Copiar link'}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-black/10 bg-white text-black transition-colors hover:bg-neutral-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {ensureLink.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : copied ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </div>

        {/* Ações secundárias */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleQrOpen}
            disabled={ensureLink.isPending || isEnded}
            title={isEnded ? 'Campanha encerrada' : 'Gerar QR Code do link'}
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[12px] font-medium text-black transition-colors hover:bg-neutral-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <QrCode className="h-3.5 w-3.5" />
            QR Code
          </button>
          <button
            type="button"
            onClick={handleShareWhatsapp}
            disabled={isSharingWhatsapp || ensureLink.isPending || isEnded}
            title={isEnded ? 'Campanha encerrada' : 'Compartilhar no WhatsApp'}
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[12px] font-medium text-black transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSharingWhatsapp ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Share2 className="h-3.5 w-3.5" />
            )}
            Compartilhar
          </button>
          {c.has_terms && (
            <button
              type="button"
              onClick={() => setTermsOpen(true)}
              title="Ver regras desta campanha"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[12px] font-medium text-black transition-colors hover:bg-neutral-50"
            >
              <FileText className="h-3.5 w-3.5" />
              Regras
            </button>
          )}
        </div>
      </div>

      {/* Diálogos */}
      {c.link_id && (
        <QrCodeDialog
          open={qrOpen}
          onOpenChange={setQrOpen}
          linkId={c.link_id}
          downloadFileName={c.short_code ?? c.slug ?? c.campaign_slug}
        />
      )}
      <CampaignTermsDialog
        open={termsOpen}
        onOpenChange={setTermsOpen}
        campaignId={c.campaign_id}
        campaignName={c.campaign_name}
      />

      {/* -----------------------------------------------------------------
          Preview de vídeo em lightbox próprio.
          - Renderiza o player com `object-contain` em fundo preto,
            garantindo que o vídeo apareça inteiro independente da
            proporção (sem cortar bordas como acontecia com o
            fullscreen nativo + `object-cover`).
          - max-w / max-h em `vw`/`dvh` se adaptam ao desktop e mobile.
          ----------------------------------------------------------------- */}
      {hasCover && isVideo && (
        <Dialog open={videoPreviewOpen} onOpenChange={setVideoPreviewOpen}>
          <DialogContent
            className="max-w-[96vw] sm:max-w-[92vw] w-[96vw] sm:w-auto p-0 gap-0 border-0 bg-black shadow-2xl overflow-hidden"
          >
            <VisuallyHidden>
              <DialogTitle>{c.campaign_name}</DialogTitle>
            </VisuallyHidden>
            {/* Botão de fechar customizado (sobrepõe o padrão por contraste) */}
            <button
              type="button"
              onClick={() => setVideoPreviewOpen(false)}
              aria-label="Fechar"
              className="absolute right-3 top-3 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex w-full items-center justify-center bg-black">
              <video
                key={c.cover_media_url}
                src={c.cover_media_url!}
                controls
                autoPlay
                playsInline
                preload="metadata"
                // object-contain garante que o vídeo nunca seja cortado.
                // max-h em dvh evita estouro em mobile com address bar.
                className="block h-auto w-auto max-h-[88dvh] max-w-[96vw] object-contain"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Subcomponente: célula de KPI (estilo editorial monocromático)
// -----------------------------------------------------------------------------

/**
 * Célula simples de KPI: valor (em destaque) acima e label discreto abaixo.
 * Sem ícones, chips, cores ou caixas — estética de índice editorial,
 * coerente com o restante do card preto/branco.
 *
 * `truncate` + `title` no valor evitam quebras quando a string é longa
 * (ex.: "R$ 1.234.567,89").
 */
function KpiCell({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-0.5 px-2 py-2.5 min-w-0">
      <span
        title={value}
        className="block w-full truncate text-center tabular-nums text-base font-semibold leading-tight text-[hsl(var(--aff-foreground))]"
      >
        {value}
      </span>
      <span className="flex items-center justify-center gap-1 w-full text-center text-[11px] text-[hsl(var(--aff-muted-foreground))]">
        <span className="truncate">{label}</span>
        {hint && <KpiInfoTooltip label={label} hint={hint} />}
      </span>
    </div>
  );
}
